export type ProjectStatus = 'active' | 'on_hold' | 'completed' | 'cancelled';
export type ProjectPriority = 'low' | 'medium' | 'high' | 'critical';

/** Department codes validated by the backend (DEPTS_VALID). */
export type ProjectDepartment = 'it' | 'digital_marketing' | 'sales' | 'hr';

/** Populated assignee subdocument returned by the list endpoint. */
export interface AssignedUser {
  id?: string;
  _id?: string;
  display_name?: string;
}

/** Milestone subdocument (spec §5) — stored inline on the project. */
export interface ProjectMilestone {
  id: string;
  title: string;
  due_date: string;
  done: boolean;
}

/** Sprint subdocument (spec §5) — stored inline on the project. */
export interface ProjectSprint {
  id: string;
  name: string;
  start: string;
  end: string;
}

export interface Project {
  id: string;
  name: string;
  department: string;
  description?: string;
  client?: string;
  status: ProjectStatus;
  priority: ProjectPriority;
  deadline?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  project_value?: number;
  reference_url?: string;
  /** May be a populated array, a raw id array, or null depending on source. */
  assigned_to?: string[] | AssignedUser[] | null;
  assignees?: AssignedUser[];
  assignee_name?: string;
  creator_name?: string;
  created_by?: string;
  created_at?: string;
  /** Server-computed progress counters — read-only. */
  task_count?: number;
  done_count?: number;
  milestones?: ProjectMilestone[];
  sprints?: ProjectSprint[];
}

export interface ProjectsFilters {
  /** Department filter — only honoured by the backend for privileged users. */
  dept?: string;
}

export interface CreateProjectPayload {
  name: string;
  department: string;
  priority: ProjectPriority;
  status: ProjectStatus;
  deadline?: string | null;
  description?: string;
  project_value?: number;
  assigned_to?: string[];
  reference_url?: string;
}

/** Edit payload — department is intentionally omitted (immutable server-side). */
export type UpdateProjectPayload = Partial<Omit<CreateProjectPayload, 'department'>> & {
  milestones?: ProjectMilestone[];
  sprints?: ProjectSprint[];
};

export interface UserOption {
  id: string;
  display_name: string;
  role: string;
  department?: string;
  username?: string;
}

/** Extract the assignee ids regardless of whether it is populated or raw. */
export function getAssignedIds(assigned: Project['assigned_to']): string[] {
  if (!assigned) return [];
  if (Array.isArray(assigned)) {
    return assigned.map((a: string | AssignedUser) => {
      if (typeof a === 'string') return a;
      return a.id ?? a._id ?? '';
    }).filter(Boolean);
  }
  // `assigned_to` is always a string[]/AssignedUser[] or null per the type, so
  // any non-array, non-null value is unreachable — nothing more to extract.
  return [];
}

/** Legacy extraction: extract the first assignee ID or empty string. */
export function getAssignedId(assigned: Project['assigned_to']): string {
  const ids = getAssignedIds(assigned);
  return ids[0] ?? '';
}
